/**
 * ================================================================================================
 *  „הפניות שלי” — the enquiries this office raised, what was answered, and the reply
 * ================================================================================================
 *
 * ── WHY THIS SCREEN DID NOT EXIST, AND WHAT THAT COST ───────────────────────────────────────────
 *
 * Measured in the route table before anything was built: the office surface carried EXACTLY ONE
 * support operation — `tenant.support_ticket.open`, a POST. There was no read of any kind. A
 * person could raise an enquiry, be shown its number once on a confirmation, close the tab, and
 * have no way back to it.
 *
 * ── AND WHY A LIST ALONE WAS NOT THE ANSWER EITHER ──────────────────────────────────────────────
 *
 * **The Owner, 2026-11-04:** *"התשובות נכתבות בתוך GLPOINT ונשמרות בפנייה, עם שרשור שהלקוח יכול
 * לקרוא ולהשיב בו בהתאם להרשאות."*
 *
 * The first version of this screen was a list and nothing else: it could show that an enquiry was
 * „נענתה” while the answer itself was nowhere in the estate — it had reached somebody by telephone
 * or in a private mailbox. 0057 gives the enquiry a thread, and this screen is where an office
 * reads it and writes into it.
 *
 * ── WHOSE ENQUIRIES, AND WHY THE SCREEN SAYS SO ─────────────────────────────────────────────────
 *
 * **Asked directly:** *"משתמש רגיל רואה את פניותיו ומנהל מורשה את פניות הארגון."*
 *
 * `support.ticket.read_all` decides it, SERVER-SIDE, and the answer carries `scope` so this
 * screen can name which list it is drawing. A list that silently held fewer rows for one reader
 * than for another is one somebody reads as *"my colleague's enquiry was lost"*.
 */
import type { JSX } from 'react';
import { useCallback, useState } from 'react';

import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { AppShell } from '../shell/AppShell';
import { DataTable } from '../components/DataTable';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { formatInstantHe } from '../api/adminVocabulary';
import type { TermHe } from '../api/adminVocabulary';
import type { OfficeEnquiry, OfficeEnquiryMessage } from '../api/contract';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';

const TITLE_HE = 'הפניות שלי';

/**
 * THE FIVE STATES `0055` DECLARES, EACH WITH A WORD AND A TONE.
 *
 * `ticket_state` is a CHECK constraint in the migration, so these five are the whole of it. An
 * unknown value falls through to the raw code rather than to a friendly default — an estate
 * serving a state this build does not know about is a fact worth seeing, not one worth hiding
 * behind the word „פתוחה”.
 *
 * `Tone` IS A CLOSED SET OF FOUR — ok | warn | error | muted — and `muted` is the right word for
 * "waiting", not a fifth one invented here. A5 anyway says the colour carries nothing on its own:
 * the Hebrew is always present, and the tone is chrome.
 */
const STATE_HE: Readonly<Record<string, TermHe>> = {
  open: { labelHe: 'ממתינה לטיפול', tone: 'muted' },
  assigned: { labelHe: 'בטיפול', tone: 'muted' },
  escalated: { labelHe: 'הועברה להמשך בדיקה', tone: 'warn' },
  answered: { labelHe: 'נענתה', tone: 'ok' },
  closed: { labelHe: 'נסגרה', tone: 'ok' },
};

export function TenantEnquiriesScreen(): JSX.Element {
  const read = useCallback(() => callOperation('tenant.support_ticket.mine', {}), []);
  const enquiriesRead = useServerData(read);

  /**
   * ── THE SEARCH IS OVER THE ROWS THIS READER ALREADY HAS ───────────────────────────────────────
   *
   * Not a server round trip, and that is a decision rather than a shortcut: the server already
   * decided WHICH enquiries this person may see, and it returns them. Filtering here cannot widen
   * that set — there is nothing to widen it to — and a search that posted the typed words back
   * would send somebody's description of their own problem over the wire a second time, for an
   * answer already in the browser.
   */
  const [typed, setTyped] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  return (
    <AppShell titleHe={TITLE_HE}>
      <ServerRead result={enquiriesRead.result} whatHe="את הפניות שלך" skeletonRows={5}>
        {(data) => {
          const needle = typed.trim().toLowerCase();
          const shown =
            needle === ''
              ? data.enquiries
              : data.enquiries.filter(
                  (e) =>
                    e.displayNo.toLowerCase().includes(needle) ||
                    e.subject.toLowerCase().includes(needle) ||
                    e.body.toLowerCase().includes(needle),
                );

          return (
            <>
              <label className="field" data-area="enquiry-search">
                <span className="field__label">חיפוש בפניות</span>
                <input
                  className="field__input"
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                />
                {/*
                  THE BODY IS SEARCHED AND THE HINT SAYS SO. Somebody looking for an enquiry they
                  raised three weeks ago remembers what they WROTE, not the category label the bot
                  put on it.
                */}
                <span className="field__hint">מספר פנייה, נושא או כל מילה מתוך מה שכתבת.</span>
              </label>

              {shown.length > 0 || data.enquiries.length === 0 ? null : (
                /*
                  A FILTER THAT MATCHED NOTHING IS AN ANSWER, and it is not the same sentence as
                  „you have no enquiries". It carries the act that undoes it.
                */
                <StateBlock
                  titleHe="אין פנייה שמתאימה לחיפוש"
                  bodyHe={`יש ${String(data.enquiries.length)} פניות ברשימה.`}
                  tone="empty"
                  nextStep={
                    <p>
                      <button type="button" className="btn btn--small" onClick={() => setTyped('')}>
                        ניקוי החיפוש
                      </button>
                    </p>
                  }
                />
              )}

              {shown.length === 0 && data.enquiries.length > 0 ? null : (
                <DataTable<OfficeEnquiry>
                  captionHe={TITLE_HE}
                  columns={[
                    {
                      key: 'no',
                      headerHe: 'מספר',
                      /*
                        THE NUMBER IS THE WAY IN. It is the thing the acknowledgement mail carries
                        and the thing a person quotes on the telephone, so it is also the control
                        that opens the enquiry — rather than a separate „פתיחה” column, which is a
                        second thing to find.
                      */
                      render: (e) => (
                        <button
                          type="button"
                          className="grid__row-open"
                          aria-expanded={open === e.ticketUid}
                          onClick={() => setOpen(open === e.ticketUid ? null : e.ticketUid)}
                        >
                          <span dir="ltr">{e.displayNo}</span>
                        </button>
                      ),
                    },
                    { key: 'subject', headerHe: 'נושא', render: (e) => e.subject },
                    {
                      key: 'state',
                      headerHe: 'מצב',
                      render: (e) => {
                        const state = STATE_HE[e.state];
                        return state === undefined ? (
                          <span dir="ltr">{e.state}</span>
                        ) : (
                          <StatusChip labelHe={state.labelHe} tone={state.tone} />
                        );
                      },
                    },
                    {
                      key: 'opened',
                      headerHe: 'נפתחה',
                      render: (e) => <span dir="ltr">{formatInstantHe(e.openedAt)}</span>,
                    },
                    {
                      key: 'answered',
                      headerHe: 'תגובה ראשונה',
                      /*
                        NOT A DASH FOR AN UNANSWERED ENQUIRY, AND NOT A BLANK EITHER. `null` here
                        means nobody has answered YET, which is a different fact from „אין נתון" —
                        so the cell says the target instead, which is what the reader wants.
                      */
                      render: (e) =>
                        e.firstResponseAt === null ? (
                          <span className="cell-note">
                            {'יעד: '}
                            <bdi>{formatInstantHe(e.respondByAt)}</bdi>
                          </span>
                        ) : (
                          <span dir="ltr">{formatInstantHe(e.firstResponseAt)}</span>
                        ),
                    },
                    {
                      key: 'by',
                      headerHe: 'נפתחה על ידי',
                      render: (e) => e.openedByDisplayName ?? <span className="cell-null">—</span>,
                    },
                  ]}
                  rows={shown}
                  rowKey={(e) => e.ticketUid}
                  /*
                   * THE SCOPE, IN WORDS, ON THE SCREEN. The server decided it; this says which
                   * list is being drawn, so a reader who sees fewer rows than a colleague knows
                   * why. The count is of what is SHOWN, with the total beside it when a search is
                   * narrowing — a figure that silently meant two different things is the defect
                   * „313 is not 313" is named for.
                   */
                  filterSummaryHe={
                    needle === ''
                      ? scopeSentence(data.scope, data.enquiries.length)
                      : `מציג ${String(shown.length)} מתוך ${String(data.enquiries.length)} פניות`
                  }
                  emptyStateHe={
                    data.scope === 'office'
                      ? 'לא נפתחו פניות בארגון הזה. פנייה נפתחת מתוך עוזר העזרה, בתחתית המסך.'
                      : 'לא פתחת פניות. פנייה נפתחת מתוך עוזר העזרה, בתחתית המסך.'
                  }
                />
              )}

              {open === null ? null : (
                <EnquiryDetail
                  enquiry={shown.find((e) => e.ticketUid === open) ?? null}
                  onReplied={() => enquiriesRead.reload()}
                />
              )}
            </>
          );
        }}
      </ServerRead>
    </AppShell>
  );
}

function scopeSentence(scope: 'office' | 'own', total: number): string {
  return scope === 'office'
    ? `מציג ${String(total)} פניות — כל הפניות של הארגון, כמנהל הארגון`
    : `מציג ${String(total)} פניות — הפניות שפתחת. מנהל הארגון רואה את כל פניות הארגון`;
}

/**
 * ONE ENQUIRY, OPENED: the words that were asked, everything said since, and the reply box.
 *
 * ── THE THREAD IS FETCHED WHEN IT IS OPENED ─────────────────────────────────────────────────────
 *
 * Not with the list. A reader opens one enquiry out of however many they have, and fetching every
 * thread to draw a table would read a conversation nobody asked for — on a surface where every row
 * is somebody's description of a problem.
 */
function EnquiryDetail({
  enquiry,
  onReplied,
}: {
  readonly enquiry: OfficeEnquiry | null;
  readonly onReplied: () => void;
}): JSX.Element | null {
  const ticketUid = enquiry?.ticketUid ?? '';
  const read = useCallback(
    () => callOperation('tenant.support_ticket.thread', { params: { ticket_uid: ticketUid } }),
    [ticketUid],
  );
  const threadRead = useServerData(read);

  const [typed, setTyped] = useState('');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);

  if (enquiry === null) return null;

  async function send(): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      await callOperation('tenant.support_ticket.reply', {
        params: { ticket_uid: ticketUid },
        body: { body: typed },
      });
      setTyped('');
      threadRead.reload();
      /* The list carries the state and the first-response time, and both can have moved. */
      onReplied();
    } catch (error: unknown) {
      setFailure(classifyActionError(error));
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="notice" data-tone="info" data-area="enquiry-detail">
      <h2 className="notice__title">
        <span dir="ltr">{enquiry.displayNo}</span>
        {` · ${enquiry.subject}`}
      </h2>

      {/*
        THE WORDS THAT WERE ASKED, FIRST AND ALWAYS. They are the start of the conversation, they
        come with the list, and showing them before the thread arrives means the panel says
        something useful on its first paint rather than a skeleton.
      */}
      <article className="thread__message" data-side="office">
        <p className="cell-note">
          {enquiry.openedByDisplayName ?? 'משתמש בארגון'}
          {' · '}
          <bdi>{formatInstantHe(enquiry.openedAt)}</bdi>
        </p>
        <p className="thread__body">{enquiry.body}</p>
      </article>

      <ServerRead result={threadRead.result} whatHe="את ההתכתבות בפנייה" skeletonRows={2}>
        {(thread) => (
          <>
            {thread.messages.map((message) => (
              <ThreadMessage key={message.messageUid} message={message} />
            ))}

            {failure === null ? null : <ActionFailureNotice failure={failure} />}

            {!thread.mayReply ? (
              /*
                A CLOSED ENQUIRY IS READ AND NOT WRITTEN, and the sentence says which. `mayReply`
                is the SERVER's answer, so a box drawn here could never be contradicted by the
                first press.
              */
              <p className="notice__body">
                הפנייה נסגרה. אפשר לקרוא אותה, ולפתוח פנייה חדשה אם הנושא חזר.
              </p>
            ) : (
              <>
                <label className="field">
                  <span className="field__label">תשובה</span>
                  <textarea
                    className="field__input"
                    rows={3}
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                  />
                </label>
                <div className="actions-row">
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={sending || typed.trim() === ''}
                    onClick={() => void send()}
                  >
                    {sending ? 'שולח…' : 'שליחת תשובה'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </ServerRead>
    </section>
  );
}

/**
 * ONE MESSAGE, DRAWN ON ITS OWN SIDE.
 *
 * `authorSide` IS THE SERVER'S and is written when the words are saved, never from a request — so
 * an office cannot write an answer in our name. An unknown value is drawn NEUTRALLY rather than
 * as one of the two: a side this build does not know about is a fact worth not guessing at.
 */
function ThreadMessage({ message }: { readonly message: OfficeEnquiryMessage }): JSX.Element {
  const ours = message.authorSide === 'platform';
  const side = message.authorSide === 'office' || ours ? message.authorSide : 'unknown';
  return (
    <article className="thread__message" data-side={side}>
      <p className="cell-note">
        {ours ? 'צוות GL-POINT' : (message.authorDisplayName ?? 'משתמש בארגון')}
        {' · '}
        <bdi>{formatInstantHe(message.createdAt)}</bdi>
      </p>
      <p className="thread__body">{message.body}</p>
    </article>
  );
}
